// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="NavItem" set="mytaible-shadcn-studio" verified=tbd

import { Slot } from '@radix-ui/react-slot';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';
import { Badge } from '../atoms/badge';

// ─── NavItem ──────────────────────────────────────────────────────────────────

export interface NavItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Lucide icon component reference */
  icon?: LucideIcon;
  /** Nav item label */
  label: string;
  /** Active / selected state */
  active?: boolean;
  /** Badge count or label shown on the right */
  badge?: string | number;
  /** Show a trailing chevron (sub-menu or external link indicator) */
  arrow?: boolean;
  /** Collapse to icon-only (used when sidebar is collapsed) */
  collapsed?: boolean;
  /** Render as child element via Radix Slot (for router Link integration) */
  asChild?: boolean;
}

const NavItem = React.forwardRef<HTMLAnchorElement, NavItemProps>(
  (
    {
      className,
      icon: Icon,
      label,
      active = false,
      badge,
      arrow = false,
      collapsed = false,
      asChild = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'a';

    return (
      <Comp
        ref={ref}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'group flex w-full items-center gap-2 rounded-r-[var(--radius)] text-sm font-medium transition-colors',
          'border-l-2',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
          collapsed ? 'justify-center px-2 py-2' : 'px-3 py-2',
          active
            ? 'border-sidebar-primary bg-sidebar-foreground/[0.06] text-sidebar-primary font-semibold'
            : 'border-transparent text-sidebar-foreground hover:bg-sidebar-foreground/[0.03] hover:text-sidebar-primary',
          className,
        )}
        {...props}
      >
        {Icon && (
          <Icon className={cn('shrink-0', collapsed ? 'size-5' : 'size-4')} aria-hidden="true" />
        )}

        {!collapsed && (
          <>
            <span className="flex-1 truncate">{label}</span>

            {badge !== undefined && (
              <Badge
                variant="primary"
                appearance="solid"
                size="sm"
                className="ml-auto shrink-0 tabular-nums"
              >
                {badge}
              </Badge>
            )}

            {arrow && !badge && (
              <ChevronRight
                className="ml-auto size-3.5 shrink-0 opacity-40 transition-transform group-hover:opacity-60"
                aria-hidden="true"
              />
            )}
          </>
        )}

        {/* Collapsed tooltip via title attribute */}
        {collapsed && <span className="sr-only">{label}</span>}
      </Comp>
    );
  },
);
NavItem.displayName = 'NavItem';

// ─── NavSection ───────────────────────────────────────────────────────────────

export interface NavSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional section group label */
  label?: string;
  /** When true, hides the label (used in collapsed sidebar) */
  collapsed?: boolean;
}

const NavSection = React.forwardRef<HTMLDivElement, NavSectionProps>(
  ({ className, label, collapsed = false, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-0.5', className)} {...props}>
      {label && !collapsed && (
        <p className="mb-1 px-3 text-xs font-medium uppercase tracking-wider text-sidebar-foreground/50 select-none">
          {label}
        </p>
      )}
      {children}
    </div>
  ),
);
NavSection.displayName = 'NavSection';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { NavItem, NavSection };
